import { memo, useMemo } from 'react';
import { Avatar, Title, Icon } from '@ui/components';
import { IFanConversationSet } from '@ui/components';

interface IGroupInfoConversationHeaderProps {
  conversation: IFanConversationSet['conversation'];
  memberCount: number;
}

export const GroupInfoConversationHeader = memo(
  ({ conversation, memberCount }: IGroupInfoConversationHeaderProps) => {
    const formattedDate = useMemo(() => {
      if (!conversation?.created_at) return null;
      return new Date(conversation.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }, [conversation?.created_at]);

    return (
      <div className="relative px-6 py-8 bg-gradient-to-br from-orange-50 via-white to-gray-50 border-b border-gray-200 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-100 to-transparent rounded-full blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-100 to-transparent rounded-full blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10">
          <div className="flex items-center justify-center mb-5">
            {conversation?.miscellaneous?.creatorAvatarUrl ? (
              <div className="relative">
                <Avatar
                  src={conversation.miscellaneous.creatorAvatarUrl}
                  size="m"
                  displayName={conversation.name}
                  className="ring-4 ring-white shadow-xl"
                />
              </div>
            ) : (
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 via-orange-500 to-pink-500 flex items-center justify-center shadow-xl ring-4 ring-white">
                  <Icon type="people" className="text-white w-10 h-10" />
                </div>
              </div>
            )}
          </div>

          <Title type="sm" className="text-center mb-2 text-gray-900 font-bold">
            {conversation?.name || 'Conversation'}
          </Title>

          <div className="flex items-center justify-center gap-8 mb-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-orange-100 to-orange-50 mb-1">
                <div className="text-lg font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                  {memberCount}
                </div>
              </div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Members
              </div>
            </div>
            {formattedDate && (
              <>
                <div className="h-10 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
                <div className="text-center">
                  <div className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-white shadow-sm border border-gray-200 mb-1">
                    <div className="text-xs font-semibold text-gray-700">{formattedDate}</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Created
                  </div>
                </div>
              </>
            )}
          </div>

          {conversation?.description && (
            <div className="max-w-md mx-auto px-4 py-3 rounded-lg bg-white/60 backdrop-blur-sm border border-gray-100">
              <p className="text-sm text-gray-700 text-center leading-relaxed line-clamp-2">
                {conversation.description}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  },
);

GroupInfoConversationHeader.displayName = 'GroupInfoConversationHeader';
